import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  MenuToggle,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, ShieldAltIcon, VirtualMachineIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { VmReplicaSummary } from '../../../types/models';
import { useVirtualMachines } from '../../../hooks/useVirtualMachines';
import { useRoles } from '../../../contexts/roles/RolesContext';
import renderVmStatusLabel from './vmStatusLabel';

function VirtualMachines() {
  const auth = useAuth();
  const { roles } = useRoles();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [isRoleSelectOpen, setIsRoleSelectOpen] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const offset = (page - 1) * perPage;
  const { vms, count, loading, error, refetch } = useVirtualMachines(perPage, offset, debouncedSearch, selectedRole);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const onRoleSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    const slug = value as string;
    setSelectedRole(slug === 'all' ? '' : slug);
    setIsRoleSelectOpen(false);
    setPage(1);
  };

  const selectedRoleName = selectedRole
    ? (roles.find((r) => r.slug === selectedRole)?.name ?? selectedRole)
    : 'All roles';

  const renderRoleLabel = (roleName: string | null, roleId: number | null) => {
    if (!roleName) {
      return '—';
    }

    const role = roleId !== null ? roles.find((r) => r.id === roleId) : undefined;
    if (role?.color) {
      const bgColor = `#${role.color}`;
      const luminance =
        parseInt(role.color.substring(0, 2), 16) * 0.299 +
        parseInt(role.color.substring(2, 4), 16) * 0.587 +
        parseInt(role.color.substring(4, 6), 16) * 0.114;
      const textColor = luminance > 186 ? '#000000' : '#ffffff';

      return (
        <Label
          style={
            {
              '--pf-v6-c-label__content--Color': textColor,
              '--pf-v6-c-label--BackgroundColor': bgColor,
            } as React.CSSProperties
          }
        >
          {roleName}
        </Label>
      );
    }

    return <Label>{roleName}</Label>;
  };

  const renderProtectionLabel = (replica: VmReplicaSummary) => {
    const isOnBackup = replica.status === 'active';
    const stateText = isOnBackup ? 'Running on backup' : 'Standby';
    const tooltipContent = `Protected — DRP replica on ${replica.device?.name ?? 'unknown hypervisor'} (${stateText})`;

    return (
      <Tooltip content={tooltipContent}>
        {isOnBackup ? (
          <Label isCompact status="warning" icon={<ShieldAltIcon />} className="pf-v6-u-ml-sm">
            On backup
          </Label>
        ) : (
          <Label isCompact color="green" icon={<ShieldAltIcon />} className="pf-v6-u-ml-sm">
            Protected
          </Label>
        )}
      </Tooltip>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load virtual machines"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (vms.length === 0) {
      return (
        <EmptyState titleText="No virtual machines found" icon={VirtualMachineIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No virtual machines are currently available.</EmptyStateBody>
        </EmptyState>
      );
    }

    return (
      <>
        <Table aria-label="Virtual machines list" variant="compact">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Role</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {vms.map((vm) => (
              <Tr key={vm.id ?? vm.name}>
                <Td dataLabel="Name">
                  <Link to={`/datacenter/virtual-machines/${vm.id}`}>{vm.name}</Link>
                  {vm.protected && vm.replica && renderProtectionLabel(vm.replica)}
                </Td>
                <Td dataLabel="Role">{renderRoleLabel(vm.role?.name ?? null, vm.role?.id ?? null)}</Td>
                <Td dataLabel="Status">{renderVmStatusLabel(vm.status)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Pagination
          itemCount={count}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Virtual Machines</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <VirtualMachineIcon />
          </Icon>
          Virtual Machines
        </Content>
        <Content component="p">Below is the list of your virtual machines</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by name..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                isOpen={isRoleSelectOpen}
                selected={selectedRole || 'all'}
                onSelect={onRoleSelect}
                onOpenChange={(isOpen) => setIsRoleSelectOpen(isOpen)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsRoleSelectOpen(!isRoleSelectOpen)}
                    isExpanded={isRoleSelectOpen}
                    aria-label="Filter by role"
                  >
                    {selectedRoleName}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  <SelectOption value="all">All roles</SelectOption>
                  {roles.map((role) => (
                    <SelectOption key={role.slug} value={role.slug}>
                      {role.name}
                    </SelectOption>
                  ))}
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={count}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {renderContent()}
      </PageSection>
    </>
  );
}

export { VirtualMachines };
